import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { ACCOUNT_NUMBER_MAX_LENGTH, sanitizeBankNameInput } from '../../../components/validations/validations';
import { createBankAccount } from '../../../services/bank-account-services';
import { openToast } from '../../../store/reducers/toastSlice';
import { getCountryCode } from '../../../utils/helper';
import { validateAddBankForm } from '../contractDetailsHelpers';

export const useAddBankAccountForm = ({
    onAccountAdded,
}: {
    onAccountAdded: (shouldReopenAutoPay: boolean) => void;
}) => {
    const dispatch = useDispatch();

    const [addBankDrawerOpen, setAddBankDrawerOpen] = useState(false);
    const [reopenAutoPayAfterAddBank, setReopenAutoPayAfterAddBank] = useState(false);
    const [addBankCountry, setAddBankCountry] = useState('');
    const [addBankFormData, setAddBankFormData] = useState({
        accountType: '',
        nickName: '',
        bankName: '',
        routingNumber: '',
        reEnterRoutingNumber: '',
        accountNumber: '',
        reEnterAccountNumber: '',
    });
    const [addBankFormErrors, setAddBankFormErrors] = useState<Record<string, string>>({});
    const [showAddBankAccountNumber, setShowAddBankAccountNumber] = useState(false);
    const [showAddBankReEnterAccountNumber, setShowAddBankReEnterAccountNumber] = useState(false);
    const [showAddBankRoutingNumber, setShowAddBankRoutingNumber] = useState(false);
    const [showAddBankReEnterRoutingNumber, setShowAddBankReEnterRoutingNumber] = useState(false);

    const handleAddBankInputChange = (field: string, value: string) => {
        if (field === 'bankName' || field === 'nickName') value = sanitizeBankNameInput(value);
        if (field === 'accountNumber' || field === 'reEnterAccountNumber') {
            value = value.replace(/[^0-9]/g, '').slice(0, ACCOUNT_NUMBER_MAX_LENGTH);
        }
        if (field === 'routingNumber' || field === 'reEnterRoutingNumber') {
            value = value.replace(/\D/g, '').slice(0, 9);
        }
        setAddBankFormData(prev => ({ ...prev, [field]: value }));
        if (addBankFormErrors[field]) {
            setAddBankFormErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleCloseAddBankDrawer = () => {
        setAddBankDrawerOpen(false);
        setReopenAutoPayAfterAddBank(false);
        setAddBankCountry('');
        setAddBankFormData({
            accountType: '',
            nickName: '',
            bankName: '',
            routingNumber: '',
            reEnterRoutingNumber: '',
            accountNumber: '',
            reEnterAccountNumber: '',
        });
        setAddBankFormErrors({});
        setShowAddBankAccountNumber(false);
        setShowAddBankReEnterAccountNumber(false);
        setShowAddBankRoutingNumber(false);
        setShowAddBankReEnterRoutingNumber(false);
    };

    const showAddBankAccountToast = (message: string, severity: 'success' | 'error') => {
        dispatch(
            openToast({
                show: true,
                isAlertOpen: true,
                message,
                severity,
            }),
        );
    };

    const handleAddBankAccountSubmit = async () => {
        const newErrors = validateAddBankForm(addBankFormData, addBankCountry);
        if (Object.keys(newErrors).length > 0) {
            setAddBankFormErrors(newErrors);
            return;
        }

        try {
            const payload = {
                bankName: addBankFormData.bankName.trim(),
                accountNumber: addBankFormData.accountNumber.trim(),
                country: getCountryCode(addBankCountry),
                accountType: addBankFormData.accountType,
                routingNumber: addBankFormData.routingNumber.trim(),
                nickName: addBankFormData.nickName.trim() || null,
            };
            const response = await createBankAccount(payload, true);

            if (response.statusCode !== 200 && response.statusCode !== 201) {
                showAddBankAccountToast(response.message || 'Failed to Add Bank Account', 'error');
                return;
            }

            showAddBankAccountToast(response.message || 'Bank Account Added Successfully', 'success');
            const shouldReopenAutoPay = reopenAutoPayAfterAddBank;
            handleCloseAddBankDrawer();
            if (shouldReopenAutoPay) {
                onAccountAdded(shouldReopenAutoPay);
            }
        } catch (error) {
            const e = error as { message?: string };
            showAddBankAccountToast(e?.message || 'Failed to Add Bank Account. Please Try Again.', 'error');
        }
    };

    const openDrawerForAutoPayFallback = () => {
        setAddBankDrawerOpen(true);
        setReopenAutoPayAfterAddBank(true);
    };

    return {
        addBankDrawerOpen,
        addBankCountry,
        setAddBankCountry,
        addBankFormData,
        addBankFormErrors,
        setAddBankFormErrors,
        showAddBankAccountNumber,
        setShowAddBankAccountNumber,
        showAddBankReEnterAccountNumber,
        setShowAddBankReEnterAccountNumber,
        showAddBankRoutingNumber,
        setShowAddBankRoutingNumber,
        showAddBankReEnterRoutingNumber,
        setShowAddBankReEnterRoutingNumber,
        handleAddBankInputChange,
        handleCloseAddBankDrawer,
        handleAddBankAccountSubmit,
        openDrawerForAutoPayFallback,
    };
};
